import { useCartData } from "@/zustand/cartStore";
import { useProductData } from "@/zustand/productStore";
import glideImage from "@/func/glideImage.js";
import { useAdditionalUpsell } from "@children/zustand/additionalUpsellStore";

const CheckboxIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M16.1231 4.18117C15.5675 3.82035 14.7869 3.93186 14.3805 4.42544L8.07447 12.1125L5.97556 9.7493C5.54786 9.26933 4.76387 9.18764 4.22434 9.56631C3.68316 9.94495 3.59209 10.6436 4.01817 11.1219L8.15369 15.7812L16.3958 5.72999C16.8023 5.23477 16.6803 4.542 16.1231 4.18117Z"
      fill="white"
    />
  </svg>
);

const BenefitCheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 16C9.05058 16 10.0909 15.7931 11.0615 15.391C12.0321 14.989 12.914 14.3997 13.6569 13.6569C14.3997 12.914 14.989 12.0321 15.391 11.0615C15.7931 10.0909 16 9.05058 16 8C16 6.94943 15.7931 5.90914 15.391 4.93853C14.989 3.96793 14.3997 3.08601 13.6569 2.34315C12.914 1.60028 12.0321 1.011 11.0615 0.608964C10.0909 0.206926 9.05058 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16ZM7.79378 11.2356L12.2382 5.90222L10.8729 4.76444L7.05067 9.35022L5.07289 7.37156L3.816 8.62844L6.48267 11.2951L7.17067 11.9831L7.79378 11.2356Z"
      fill="currentColor"
    />
  </svg>
);

const CheckoutUpsell = () => {
  const settings = window.post_template?.checkout_v4_additional_upsell || {};
  const { selectedProduct } = useCartData();
  const productData = useProductData();
  const { additionalUpsellEnabled, setAdditionalUpsellEnabled } = useAdditionalUpsell();

  const quantity = Number(selectedProduct?.amount) || 1;

  const heading = settings.tr_checkout_v4_additional_upsell_heading;
  const badgeText = settings.tr_checkout_v4_additional_upsell_badge_text;
  const title = settings.tr_checkout_v4_additional_upsell_title;
  const priceSuffix = settings.tr_checkout_v4_additional_upsell_price_suffix;
  const discountText = settings.tr_checkout_v4_additional_upsell_discount_text;
  const description = settings.tr_checkout_v4_additional_upsell_description;
  const benefits = settings.checkout_v4_additional_upsell_benefits || [];
  const image = settings.checkout_v4_additional_upsell_image;

  const unitPrice = Number(settings.checkout_v4_additional_upsell_price);
  const oldUnitPrice = Number(settings.checkout_v4_additional_upsell_old_price);
  const oldTotalPrice = oldUnitPrice > 0 ? oldUnitPrice * quantity : 0;

  const formatMoney = (value) => {
    const formatted = Number(value).toFixed(2);
    return productData?.formatted_string
      ? productData.formatted_string.replace(/(0\.00|0,00|0)/, formatted)
      : `$${formatted}`;
  };

  const colors = {
    "--checkoutUpsell-accent": settings.checkout_v4_additional_upsell_accent_color || "#E16203",
    "--checkoutUpsell-bg": settings.checkout_v4_additional_upsell_bg_color || "#FDF7F2",
    "--checkoutUpsell-badge-bg": settings.checkout_v4_additional_upsell_badge_bg_color || "#1F7A5A",
    "--checkoutUpsell-badge-text": settings.checkout_v4_additional_upsell_badge_text_color || "#FFFFFF",
    "--checkoutUpsell-discount": settings.checkout_v4_additional_upsell_discount_color || "#FF0000",
    "--checkoutUpsell-text": settings.checkout_v4_additional_upsell_text_color || "#333333",
  };

  return (
    <section className="checkoutUpsell" style={colors} data-cy="cc-checkout-upsell">
      {heading && <div className="checkoutUpsell__heading">{heading}</div>}
      <div
        className={`checkoutUpsell__card ${additionalUpsellEnabled ? "checkoutUpsell__card--checked" : ""}`}
        onClick={() => setAdditionalUpsellEnabled(!additionalUpsellEnabled)}
        role="checkbox"
        aria-checked={additionalUpsellEnabled}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            setAdditionalUpsellEnabled(!additionalUpsellEnabled);
          }
        }}
        data-cy="cc-checkout-upsell-card"
      >
        <div className="checkoutUpsell__row">
          <div className="checkoutUpsell__product">
            <span className={`checkoutUpsell__checkbox ${additionalUpsellEnabled ? "_checked" : ""}`}>
              {additionalUpsellEnabled && <CheckboxIcon />}
            </span>
            {image?.url && (
              <img
                className="checkoutUpsell__image"
                src={glideImage(image.url, { h: 192 })}
                alt={image.alt || title || ""}
              />
            )}
            <div className="checkoutUpsell__labels">
              {badgeText && <span className="checkoutUpsell__badge">{badgeText}</span>}
              <span className="checkoutUpsell__title" data-cy="cc-checkout-upsell-title">
                {quantity}x {title}
              </span>
            </div>
          </div>
          <div className="checkoutUpsell__prices">
            {oldTotalPrice > 0 && <span className="checkoutUpsell__oldPrice">{formatMoney(oldTotalPrice)}</span>}
            <span className="checkoutUpsell__price">
              {formatMoney(unitPrice)}
              {priceSuffix && <span className="checkoutUpsell__priceSuffix">{priceSuffix}</span>}
            </span>
            {discountText && <span className="checkoutUpsell__discount">{discountText}</span>}
          </div>
        </div>
        {(description || benefits.length > 0) && (
          <>
            <div className="checkoutUpsell__divider"></div>
            <div className="checkoutUpsell__benefits">
              {description && <div className="checkoutUpsell__benefitsHeading">{description}</div>}
              {benefits.length > 0 && (
                <div className="checkoutUpsell__benefitsList">
                  {benefits.map((benefit, index) => (
                    <div className="checkoutUpsell__benefit" key={index}>
                      <span className="checkoutUpsell__benefitIcon">
                        <BenefitCheckIcon />
                      </span>
                      <span className="checkoutUpsell__benefitText">{benefit?.tr_benefit_text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CheckoutUpsell;
